import { ROUTES_POLLING_RATE_CONFIG } from "../routing/routes-polling-rate-config";

/**
 * Finds the closest matching route from the polling rate config for a given pathname.
 *
 * The function matches routes by finding the longest matching prefix:
 * - Exact matches are preferred (e.g., "/dashboard" matches "/dashboard")
 * - Partial matches use the longest prefix (e.g., "/messaging/inbox/sms/thread-123" matches "/messaging")
 * - If no match is found, returns "DEFAULT"
 *
 * @param pathname - The current route pathname (e.g., "/messaging/inbox/sms/thread-123")
 * @returns The closest matching route key from ROUTES_POLLING_RATE_CONFIG
 *
 * @example
 * // Get the matching route key
 * getClosestPollingRateRoute("/messaging/inbox/sms/thread-123") // returns "/messaging"
 * getClosestPollingRateRoute("/dashboard") // returns "/dashboard"
 * getClosestPollingRateRoute("/tickets") // returns "DEFAULT"
 *
 * @example
 * // Get the actual polling rate in milliseconds
 * const routeKey = getClosestPollingRateRoute(window.location.pathname);
 * const pollingRateMs = ROUTES_POLLING_RATE_CONFIG[routeKey];
 * // Use pollingRateMs for your polling interval
 */
export function getClosestPollingRateRoute(pathname: string): keyof typeof ROUTES_POLLING_RATE_CONFIG {
  // Get all configured routes except DEFAULT
  const configuredRoutes = Object.keys(ROUTES_POLLING_RATE_CONFIG).filter((route) => route !== "DEFAULT") as string[];

  // Find the longest matching prefix
  let closestMatch: string | null = null;
  let longestMatchLength = 0;

  for (const route of configuredRoutes) {
    // Check if the pathname starts with this route
    if (pathname.startsWith(route)) {
      // Ensure it's a complete path segment match (not partial segment)
      // e.g., "/messaging" should match "/messaging/inbox" but not "/messaging-new"
      const nextChar = pathname[route.length];
      if (nextChar === undefined || nextChar === "/" || nextChar === "?") {
        if (route.length > longestMatchLength) {
          closestMatch = route;
          longestMatchLength = route.length;
        }
      }
    }
  }

  // Return the closest match or DEFAULT
  return (closestMatch ?? "DEFAULT") as keyof typeof ROUTES_POLLING_RATE_CONFIG;
}
